import type {
  BrandId,
  CreateCommissionRateRequest,
  CreateCommissionRateResponse,
  ListCommissionRatesResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for `/v1/ops/commission-rates`
 * (epic #1034 E5-M2, #1144) — the commission-rate management card on the
 * lead pricing panel.
 *
 * Brand handling mirrors the server contract (same as sales costs):
 *   - lead sessions omit `brand` (ops-api pins their profile brand);
 *   - admin sessions pass an explicit brand.
 */

const BASE_PATH = '/v1/ops/commission-rates';

export const COMMISSION_RATES_QUERY_KEY = ['commission-rates'] as const;

function withBrand(path: string, brand: BrandId | null): string {
  return brand === null ? path : `${path}?brand=${encodeURIComponent(brand)}`;
}

export async function listCommissionRates(
  brand: BrandId | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListCommissionRatesResponse> {
  return opsApiRequest<ListCommissionRatesResponse>(
    withBrand(BASE_PATH, brand),
    { signal },
    fetchImpl,
  );
}

export async function createCommissionRate(
  brand: BrandId | null,
  body: CreateCommissionRateRequest,
  fetchImpl: typeof fetch,
): Promise<CreateCommissionRateResponse> {
  return opsApiRequest<CreateCommissionRateResponse>(
    withBrand(BASE_PATH, brand),
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * `enabled` gates the fetch until the session has resolved — the page
 * cannot know whether to send an explicit admin brand before it knows the
 * session roles (an early brand-less admin request would 400).
 */
export function useCommissionRates(
  brand: BrandId | null,
  enabled = true,
): UseQueryResult<ListCommissionRatesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...COMMISSION_RATES_QUERY_KEY, brand ?? 'own'],
    queryFn: ({ signal }) => listCommissionRates(brand, fetchImpl, signal),
    enabled,
  });
}

export interface CreateCommissionRateVariables {
  brand: BrandId | null;
  body: CreateCommissionRateRequest;
}

export function useCreateCommissionRate(): UseMutationResult<
  CreateCommissionRateResponse,
  Error,
  CreateCommissionRateVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brand, body }) => createCommissionRate(brand, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMMISSION_RATES_QUERY_KEY });
    },
  });
}
